import { useEffect, useRef, useState } from 'react';

export type ActivityLevel = 'active' | 'idle-1min' | 'idle-5min' | 'idle-long';

interface UseActivityDetectionOptions {
  idle1MinMs?: number;
  idle5MinMs?: number;
  idleLongMs?: number;
}

/**
 * Detects user activity (mouse, keyboard, touch)
 * Returns current activity level for adaptive sync behavior
 *
 * Activity levels:
 * - 'active': User actively typing, clicking, scrolling
 * - 'idle-1min': No activity for 1+ minute
 * - 'idle-5min': No activity for 5+ minutes
 * - 'idle-long': No activity for 15+ minutes
 *
 * Usage:
 * ```typescript
 * const activityLevel = useActivityDetection();
 *
 * // Adjust sync interval based on activity
 * const syncInterval = activityLevel === 'active' ? 10000 : 120000;
 * ```
 */
export const useActivityDetection = (
  options: UseActivityDetectionOptions = {}
): ActivityLevel => {
  const {
    idle1MinMs = 60 * 1000, // 1 minute
    idle5MinMs = 5 * 60 * 1000, // 5 minutes
    idleLongMs = 15 * 60 * 1000, // 15 minutes
  } = options;

  const [activityLevel, setActivityLevel] = useState<ActivityLevel>('active');
  const lastActivityRef = useRef<number>(Date.now());
  const checkIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const mouseMoveTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
      if (activityLevel !== 'active') {
        console.log('👤 [ACTIVITY] User became active (was:', activityLevel, ')');
        setActivityLevel('active');
      }
    };

    // Activity events to track
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    // Throttle mousemove to avoid excessive updates
    const throttledMouseMove = () => {
      if (!mouseMoveTimeoutRef.current) {
        mouseMoveTimeoutRef.current = setTimeout(() => {
          updateActivity();
          mouseMoveTimeoutRef.current = undefined;
        }, 1000); // Max once per second
      }
    };

    // Add event listeners
    const listeners: Array<[string, EventListener]> = [];

    events.forEach((event) => {
      window.addEventListener(event, updateActivity as EventListener, { passive: true });
      listeners.push([event, updateActivity as EventListener]);
    });

    // Special handling for mousemove (throttled)
    window.addEventListener('mousemove', throttledMouseMove, { passive: true });
    listeners.push(['mousemove', throttledMouseMove]);

    // Check activity level every 10 seconds
    checkIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const idleTime = now - lastActivityRef.current;
      const idleSeconds = Math.floor(idleTime / 1000);

      let newLevel: ActivityLevel;
      if (idleTime >= idleLongMs) {
        newLevel = 'idle-long';
      } else if (idleTime >= idle5MinMs) {
        newLevel = 'idle-5min';
      } else if (idleTime >= idle1MinMs) {
        newLevel = 'idle-1min';
      } else {
        newLevel = 'active';
      }

      if (newLevel !== activityLevel) {
        console.log(`👤 [ACTIVITY] Level changed: ${activityLevel} → ${newLevel} (idle for ${idleSeconds}s)`);
        setActivityLevel(newLevel);
      }
    }, 10000); // Check every 10 seconds

    return () => {
      // Cleanup event listeners
      listeners.forEach(([event, handler]) => {
        window.removeEventListener(event, handler);
      });

      // Cleanup intervals/timeouts
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (mouseMoveTimeoutRef.current) {
        clearTimeout(mouseMoveTimeoutRef.current);
      }
    };
  }, [activityLevel, idle1MinMs, idle5MinMs, idleLongMs]);

  return activityLevel;
};
